import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import photoGroups from '../../pages/Gallery/photoData';

const TimelineContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const TimelineTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 3rem;
  font-weight: 700;
  color: white;
  margin-bottom: 3rem;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const TimelineWrapper = styled.div`
  position: relative;
  padding-left: 3rem;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  }
  
  @media (max-width: 768px) {
    padding-left: 2rem;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 3rem;
  
  &::before {
    content: '';
    position: absolute;
    left: -3.5rem;
    top: 0.5rem;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.2);
  }
  
  @media (max-width: 768px) {
    &::before {
      left: -2.5rem;
    }
  }
`;

const GlassCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 1.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateX(0.5rem);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
`;

const DateBadge = styled.div`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 2rem;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  margin-bottom: 1rem;
`;

const MilestoneTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.75rem;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const MilestoneDesc = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
`;

const ReadMoreButton = styled.button`
  background: transparent;
  color: #667eea;
  border: none;
  padding: 0.5rem 0;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 0.5rem;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 2rem;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  width: 90%;
  text-align: center;
  position: relative;
`;

const ModalImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 1rem;
`;

const ImageWrapper = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const ViewImagesButton = styled.button`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(102, 126, 234, 0.9);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  font-weight: 600;
  &:hover {
    background: rgba(102, 126, 234, 1);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ModalText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.8;
  text-align: left;
  white-space: pre-line;
  margin-bottom: 1.5rem;
`;

const ModalButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  margin: 0.5rem;
  &:hover {
    opacity: 0.9;
  }
`;

const Timeline = () => {
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const navigate = useNavigate();

  const milestones = [
    {
      date: 'Dec 4, 2020',
      title: 'Inauguration of the Society',
      description: 'Souharda Co-operative Society was established and inaugurated by President Thomas T. Horta, marking the foundation of our cooperative journey.',
      fullDescription: 'Souharda Co-operative Society was established on August 19, 2020, under the Karnataka Souharda Act, 1997—a progressive law that encourages cooperative development based on self-help, mutual support, and social responsibility.\n\nFounded by Mr. Santiag T. Horta, our society was officially inaugurated on December 4, 2020, in Kelaginor, Honavar, Karnataka.\n\nUnder the leadership of President Mr. Thomas T. Horta and Vice President Mr. James J. Rodrigues, we are dedicated to uplifting our members through transparent, people-centric cooperative practices.\n\nOur mission is simple yet powerful: to enhance the well-being of our community by fostering cooperation, trust, and sustainable development. As a proud part of the Karnataka Souharda movement, we work to empower individuals, encourage financial growth, and create opportunities that benefit every member.\n\nTogether, we strive to build a stronger, more prosperous community—one where collective progress leads to shared success. Join us as we grow, support, and empower each other!',
      galleryIndex: 0
    },
    {
      date: 'Apr 21, 2025',
      title: 'Honnavar Branch Opening',
      description: 'Supreet Souharda Co-operative Society Ltd., Kelaginoor, proudly inaugurated its New Branch at Honavar on Monday, 21st April 2025.',
      fullDescription: 'Supreet Souharda Co-operative Society Ltd., Kelaginoor, proudly inaugurated its New Branch at Honavar on Monday, 21st April 2025 at Shakuntala Arcade, Bazar Road, Honavar.\n\nThe inauguration and blessing ceremony was conducted in a graceful and spiritual manner by Rev. Fr. Salvadore Gonsalves, Parish Priest of San Salvadore Church, Honavar, and Rev. Fr. Michael Pinto, Parish Priest of St. Sebastian Church, Upper Kasarkod, who invoked blessings for the success and growth of the new branch.\n\nThe event was honoured by the presence of distinguished guests Shri. S.N. Nayak, Advocate & Notary Public, and Shri. MTC Abbas, Businessman, Tonka, whose presence added importance and encouragement to the occasion.\n\nThe inauguration ceremony was attended by the President, Board of Directors, General Manager, staff members, customers, well-wishers, and members of the public. The event marked a significant milestone in the society\'s journey towards expansion and strengthening its commitment to providing reliable, transparent, and member-focused financial services.\n\nThe new Honavar Branch has been established with the objective of offering convenient and efficient financial services to the people of Honavar and nearby areas, and to further extend the Society\'s mission of service and trust.\n\nSupreet Souharda Co-operative Society Ltd. expresses its sincere gratitude to everyone who supported and participated in making this event a grand success and looks forward to serving the community with continued dedication.',
      galleryIndex: 1
    },
    {
      date: 'Nov 14, 2025',
      title: 'Kavalakki Branch Opening',
      description: 'Supreet Souharda Co-operative Society Ltd., Kelaginoor, marked another proud moment with the successful inauguration of its Kavalakki Branch on Friday, 14th November 2025.',
      fullDescription: 'Supreet Souharda Co-operative Society Ltd., Kelaginoor, marked another proud moment with the successful inauguration of its Kavalakki Branch on Friday, 14th November 2025, located at Govardhan Complex, Kavalakki.\n\nThe new branch was formally inaugurated and blessed by Rev. Fr. Wilson Noronha, Parish Priest of St. Francis Assisi Church, Hadinbal, along with Rev. Fr. David Pinto, Parish Priest of St. Sebastian Church, Upper Kasarkod. The blessing ceremony was conducted with prayers and good wishes for the prosperity and continued success of the Society.\n\nThe occasion was graced by eminent personalities including Shri. Venkatramana Hegde, Proprietor of Shreekumar Logistics; Shri. Ishwar V. Naik, President of Gram Panchayat, Kavalakki; Shri. Govinda Bhat Agni, Member of Gram Panchayat, Kavalakki; and Shri. Mahesh G. Naik, Vice President, Human Rights Commission, Honnavar Taluk. Their presence and encouraging words made the event memorable and meaningful.\n\nThe function witnessed the participation of the Society\'s President, Board of Directors, General Manager, staff, members, customers, and local residents, reflecting the strong bond between the Society and the community it serves.\n\nThe establishment of the Kavalakki Branch represents an important step in the Society\'s ongoing efforts to expand its reach and make its services more accessible to people in rural and semi-urban areas. This branch aims to provide efficient, customer-friendly, and trustworthy financial services to support the economic well-being of the local community.\n\nSupreet Souharda Co-operative Society Ltd. sincerely thanks all dignitaries, members, and well-wishers for their valuable support and cooperation and remains committed to serving the public with integrity, dedication, and excellence.',
      galleryIndex: 2
    }
  ];

  const handleCardClick = (milestone) => {
    setSelectedMilestone(milestone);
  };

  const handleViewGallery = () => {
    navigate('/gallery', { state: { scrollToIndex: selectedMilestone.galleryIndex } });
  };

  return (
    <TimelineContainer>
      <TimelineTitle>Our Journey</TimelineTitle>
      <TimelineWrapper>
        {milestones.map((milestone, index) => (
          <TimelineItem key={index}>
            <GlassCard onClick={() => handleCardClick(milestone)}>
              <DateBadge>{milestone.date}</DateBadge>
              <MilestoneTitle>{milestone.title}</MilestoneTitle>
              <MilestoneDesc>{milestone.description}</MilestoneDesc>
              {milestone.fullDescription && (
                <ReadMoreButton onClick={(e) => { e.stopPropagation(); handleCardClick(milestone); }}>Read More →</ReadMoreButton>
              )}
            </GlassCard>
          </TimelineItem>
        ))}
      </TimelineWrapper>

      {selectedMilestone && (
        <Modal onClick={() => setSelectedMilestone(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setSelectedMilestone(null)}>×</CloseButton>
            {photoGroups[selectedMilestone.galleryIndex]?.images[0] && (
              <ImageWrapper>
                <ModalImage src={photoGroups[selectedMilestone.galleryIndex].images[0].src} alt="Preview" />
                <ViewImagesButton onClick={handleViewGallery}>View Images</ViewImagesButton>
              </ImageWrapper>
            )}
            <h3 style={{ color: 'white', marginBottom: '1rem' }}>{selectedMilestone.title}</h3>
            {selectedMilestone.fullDescription ? (
              <ModalText>{selectedMilestone.fullDescription}</ModalText>
            ) : (
              <ModalText>{selectedMilestone.description}</ModalText>
            )}
          </ModalContent>
        </Modal>
      )}
    </TimelineContainer>
  );
};

export default Timeline;
